import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { setGlobalMetadata } from '@/utils/globalMetadata';
import { getMetadataForRoute } from '@/config/routeMetadata';
import { useOSMenuMetadata } from '@/hooks/useOSMenuMetadata';

/**
 * Hook that automatically applies metadata based on the current route
 * Uses the routeMetadataConfig to set appropriate title, description, and OG tags
 */
export const useRouteMetadata = () => {
  const location = useLocation();
  const { fetchMetadataByPath } = useOSMenuMetadata();

  useEffect(() => {
    const applyMetadata = async () => {
      // Try to fetch from database first
      try {
        const dbMetadata = await fetchMetadataByPath(location.pathname);
        
        if (dbMetadata && dbMetadata.visible) {
          // Use database metadata
          setGlobalMetadata({
            title: dbMetadata.seo_title || dbMetadata.route_label,
            description: dbMetadata.seo_description || dbMetadata.description || '',
            ogTitle: dbMetadata.og_title || dbMetadata.seo_title || dbMetadata.route_label,
            ogDescription: dbMetadata.og_description || dbMetadata.seo_description || dbMetadata.description || '',
            ogImage: dbMetadata.og_image || '',
            keywords: dbMetadata.seo_keywords || '',
            canonicalUrl: dbMetadata.canonical_url || window.location.href,
            favicon: '',
          });
          return;
        }
      } catch (error) {
        console.warn('Failed to fetch route metadata from database, falling back to hardcoded config:', error);
      }

      // Fallback to hardcoded routeMetadata.ts
      const routeMetadata = getMetadataForRoute(location.pathname);
      
      if (routeMetadata) {
        setGlobalMetadata({
          title: routeMetadata.title,
          description: routeMetadata.description,
          ogTitle: routeMetadata.ogTitle || routeMetadata.title,
          ogDescription: routeMetadata.ogDescription || routeMetadata.description,
          ogImage: routeMetadata.ogImage || '',
          keywords: routeMetadata.keywords || '',
          canonicalUrl: routeMetadata.canonicalUrl || window.location.href,
          favicon: '',
        });
      }
    };

    applyMetadata();
  }, [location.pathname, fetchMetadataByPath]);
};
